if (!customElements.get('pb-customer-stories')) {
  customElements.define('pb-customer-stories', class extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.events = new AbortController();
      const listen = (target, type, callback, options = {}) => target?.addEventListener(type, callback, { ...options, signal: this.events.signal });
      this.media = JSON.parse(this.querySelector('[data-community-media]')?.textContent || '[]');
      this.dialog = this.querySelector('dialog');
      this.viewer = this.querySelector('[data-community-viewer]');
      this.dialogTitle = this.querySelector('h3[data-community-title]');
      this.caption = this.querySelector('[data-community-caption]');
      this.counter = this.querySelector('[data-community-count]');
      this.nav = this.querySelector('[data-community-nav]');
      this.footer = this.querySelector('.pbh-community-dialog-foot');
      this.rail = this.querySelector('[data-community-rail]');
      this.track = this.querySelector('[data-community-track]');
      this.section = this.querySelector('.pbh-community');
      this.stripPrev = this.querySelector('[data-community-strip-prev]');
      this.stripNext = this.querySelector('[data-community-strip-next]');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.manualBrowsing = false;
      this.drift = 0;
      this.inView = false;
      this.querySelectorAll('[data-community-open]').forEach(button => {
        listen(button, 'click', () => this.open(button.dataset.communityOpen, button));
      });
      listen(this.querySelector('[data-community-close]'), 'click', () => this.dialog.close());
      listen(this.querySelector('[data-community-prev]'), 'click', () => this.move(-1));
      listen(this.querySelector('[data-community-next]'), 'click', () => this.move(1));
      listen(this.dialog, 'keydown', event => {
        if (this.group !== 'photos') return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.move(event.key === 'ArrowRight' ? 1 : -1);
        }
      });
      listen(this.dialog, 'click', event => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
      });
      listen(this.dialog, 'close', () => {
        this.viewer.querySelector('video')?.pause();
        this.viewer.replaceChildren();
        document.documentElement.classList.remove('pbh-customer-viewer-open');
        this.opener?.focus({ preventScroll: true });
      });
      listen(this.stripPrev, 'click', event => this.browse(-1, event.detail > 0));
      listen(this.stripNext, 'click', event => this.browse(1, event.detail > 0));
      listen(this.rail, 'pointerdown', () => this.stopMotion(), { passive: true });
      listen(this, 'focusin', () => this.stopMotion());
      listen(this.rail, 'wheel', event => {
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) this.stopMotion();
      }, { passive: true });
      listen(this.rail, 'scroll', () => this.updateControls(), { passive: true });
      listen(this.rail, 'keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        this.stopMotion();
        if (event.key === 'Home' || event.key === 'End') {
          this.rail.scrollTo({ left: event.key === 'Home' ? 0 : this.maxScroll(), behavior: 'instant' });
        } else {
          this.browse(event.key === 'ArrowRight' ? 1 : -1, false);
        }
      });
      listen(window, 'keydown', event => {
        if (this.inView && ['PageDown', 'PageUp', 'ArrowDown', 'ArrowUp', 'Home', 'End', ' '].includes(event.key)) this.stopMotion();
      });
      listen(window, 'scroll', () => this.requestMotion(), { passive: true });
      listen(window, 'resize', () => { this.requestMotion(); this.updateControls(); }, { passive: true });
      listen(this.reducedMotion, 'change', () => this.stopMotion());
      this.visibilityObserver = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        if (this.inView) this.requestMotion();
      });
      this.visibilityObserver.observe(this.section);
      this.sizeObserver = new ResizeObserver(() => { this.requestMotion(); this.updateControls(); });
      this.sizeObserver.observe(this.rail);
      this.sizeObserver.observe(this.track);
      this.updateControls();
    }
    disconnectedCallback() {
      this.viewer?.querySelector('video')?.pause();
      if (this.dialog?.open) this.dialog.close();
      this.events?.abort();
      this.visibilityObserver?.disconnect();
      this.sizeObserver?.disconnect();
      cancelAnimationFrame(this.motionFrame);
      document.documentElement.classList.remove('pbh-customer-viewer-open');
      this.initialised = false;
    }
    maxScroll() {
      return Math.max(0, this.rail.scrollWidth - this.rail.clientWidth);
    }
    requestMotion() {
      if (this.motionFrame || !this.inView || this.manualBrowsing || this.reducedMotion.matches || this.dialog.open || document.hidden) return;
      this.motionFrame = requestAnimationFrame(() => {
        this.motionFrame = null;
        if (this.manualBrowsing || this.reducedMotion.matches || !this.inView || this.dialog.open) return;
        const bounds = this.section.getBoundingClientRect();
        const entry = innerHeight * .85;
        const progress = Math.min(1, Math.max(0, (entry - bounds.top) / (entry + bounds.height)));
        this.drift = Math.min(this.maxScroll(), innerWidth * .6) * progress;
        this.track.style.transform = `translate3d(${-this.drift}px, 0, 0)`;
        this.updateControls();
      });
    }
    stopMotion() {
      if (this.manualBrowsing) return;
      this.manualBrowsing = true;
      cancelAnimationFrame(this.motionFrame);
      this.motionFrame = null;
      const position = this.rail.scrollLeft + this.drift;
      this.track.style.transform = '';
      this.drift = 0;
      this.rail.scrollLeft = position;
      this.updateControls();
    }
    browse(direction, pointer) {
      this.stopMotion();
      this.rail.scrollBy({ left: direction * this.rail.clientWidth * .7, behavior: pointer && !this.reducedMotion.matches ? 'smooth' : 'instant' });
    }
    updateControls() {
      const position = this.rail.scrollLeft + this.drift;
      this.stripPrev.disabled = position < 2;
      this.stripNext.disabled = position >= this.maxScroll() - 2;
    }
    open(group, opener) {
      this.stopMotion();
      this.group = group;
      this.opener = opener;
      this.dialogTitle.textContent = opener.dataset.communityTitle;
      this.nav.hidden = group !== 'photos';
      this.footer.hidden = group.startsWith('review_');
      this.dialog.dataset.mode = group === 'photos' ? 'photo' : group === 'film' ? 'film' : 'review';
      if (group === 'film') {
        const video = this.querySelector('[data-community-film]').content.querySelector('video').cloneNode(true);
        video.setAttribute('aria-label', 'Customer footage of Snow being poured, cats exploring the tray and litter being scooped');
        this.viewer.replaceChildren(video);
        this.caption.textContent = 'Snow at home, from the first pour to the next scoop.';
      } else if (group.startsWith('review_')) {
        const review = this.querySelector(`[data-community-review="${group}"]`).cloneNode(true);
        review.querySelector('button').remove();
        review.removeAttribute('data-community-review');
        review.classList.add('pbh-community-review-full');
        review.querySelectorAll('[id]').forEach(element => {
          const original = element.id;
          element.id = `${original}-expanded`;
          review.querySelectorAll('[clip-path]').forEach(clipped => {
            if (clipped.getAttribute('clip-path') === `url(#${original})`) clipped.setAttribute('clip-path', `url(#${element.id})`);
          });
        });
        const wrapper = document.createElement('div');
        wrapper.className = 'pbh-review-grid';
        wrapper.append(review);
        this.viewer.replaceChildren(wrapper);
      } else {
        this.items = this.media.filter(item => item.src);
        if (!this.items.length) return;
        this.position = Math.max(0, this.items.findIndex(item => item.filename === opener.dataset.communityFilename));
        this.showPhoto();
      }
      this.dialog.showModal();
      document.documentElement.classList.add('pbh-customer-viewer-open');
      this.querySelector('[data-community-close]').focus();
    }
    move(change) {
      if (this.group !== 'photos' || !this.items.length) return;
      this.position = (this.position + change + this.items.length) % this.items.length;
      this.showPhoto();
    }
    showPhoto() {
      const item = this.items[this.position];
      const image = document.createElement('img');
      image.src = item.src;
      image.alt = item.caption;
      image.width = item.width;
      image.height = item.height;
      image.decoding = 'async';
      this.viewer.replaceChildren(image);
      this.caption.textContent = item.caption;
      this.counter.textContent = `${this.position + 1} / ${this.items.length}`;
    }
  });
}
